// A PDF as a book: pages drawn by PDF.js (./pdf.js), turned by StPageFlip
// (page-flip, MIT) — the curl, the shadow, the drag of a corner. Two pages
// side by side where there is room, one on a phone; the cover alone, as a
// book opens.
//
// Pages are drawn when they are near: the ones on show and a few either side,
// so a long book opens as quickly as a short one and only the pages turned to
// are fetched. It fills its parent, which has to have a height.
//
// The book is given an element exactly its own size, centred by CSS, rather
// than a larger one to centre itself in: page-flip draws a turning hard page
// (the cover) at the element's top-left corner, leaving out the offset it
// gives a page at rest, so a book with room around it jumped by that offset
// for as long as the cover turned (page-flip 2.0.7, HTMLPage.drawHard).
import React, { useCallback, useEffect, useRef, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { openPdf, renderPageImage } from "./pdf"
import SoundBank from "aldoo-ra/SoundBank"
import useT from "../../hooks/useT"
import "./flipbook.css"

// the flip engine, loaded with the book (the ES module build; the package's
// default entry is a UMD bundle)
const loadPageFlip = () => import("page-flip/dist/js/page-flip.module.js").then((module) => module.PageFlip)

// how many pages either side of the ones on show are drawn ahead
const AHEAD = 3
// the pages drawn before the book is shown: the cover and the spread the
// first turn opens (the ones after it are drawn while the cover is read)
const PRELOAD = 3
// the narrowest a page may be before two side by side become one
const MIN_PAGE_WIDTH = 280

// The book's sounds: CMS SoundBank "aldoo-com-sounds" (aldoo-com-api
// scripts/seed/sounds.js). The bank is named only when a book opens, so the
// shop's other pages never load it; the samples arrive while the first pages
// are drawn. When and how loud, as heyzine's magazine flipbooks play theirs:
//   peel — a corner starts to lift (on hover or taken), quietly (0.1)
//   flip — a page turns to the next or the previous spread (0.2)
//   jump — the book turns to a page further than the next spread (0.2)
const SOUND_BANK = "aldoo-com-sounds"
const SOUNDS = {
  peel: { id: "flipbook-start", volume: 0.1 },
  flip: { id: "flipbook-flip", volume: 0.2 },
  jump: { id: "flipbook-jump", fallback: "flipbook-flip", volume: 0.2 },
}
// a turn, in ms: heyzine's (turn.js duration 1000)
const FLIP_TIME = 1000
// how near a corner the cursor must come for it to peel (and a click there to
// turn the page), in px: heyzine's (turn.js cornerSize 60). page-flip's own is a
// fifth of the page's diagonal — ~180px — and peeled from well inside the page.
const CORNER_SIZE = 60

/** page-flip's corner test, with heyzine's corner size in place of its own. */
const tightenCorners = (flip) => {
  const controller = flip.getFlipController()
  const render = flip.getRender()
  controller.isPointOnCorners = (point) => {
    const rect = controller.getBoundsRect()
    const p = render.convertToBook(point)
    const size = Math.min(CORNER_SIZE, rect.pageWidth / 3, rect.height / 3)
    return p.x > 0 && p.y > 0 && p.x < rect.width && p.y < rect.height && (p.x < size || p.x > rect.width - size) && (p.y < size || p.y > rect.height - size)
  }
}

/**
 * A turn back on a single page (a phone) as the curl of a turn forward, run
 * the other way.
 *
 * page-flip 2.0.7 turns a single page back by sliding the one before in from
 * the left edge, where its fold is off the page: it reads as a flat sheet
 * pushed across. Here the page before is laid down at once and the turn is
 * that page's forward turn in reverse — its curl opens back out over the
 * page it came to rest on. A drag works as a forward drag does, mirrored: the
 * finger at the left edge is the page fully turned, at the right edge the
 * page down; let go past the middle and it lands, short of it and it lifts
 * away again (the forward turn completes, back to the page it started on).
 *
 * A finger let go is a turn once it has come a fifth of the page in the
 * turning direction (page-flip turns only once the page is dragged past the
 * book's edge, which a finger on a phone's page never reaches: the page fell
 * back, under a flip's sound). `onTurn` is told of every release that turns a
 * page, so its sound plays for a turn and only then.
 */
const DRAG_TURN = 0.2
const curlBackTurns = (flip, { onTurn = () => {} } = {}) => {
  const controller = flip.getFlipController()
  const render = flip.getRender()
  const BACK = 1
  let reversed = false
  // the finger's first and last x in this gesture (page coordinates)
  let dragFrom = null
  let dragAt = null

  // a back turn on a single page: the page before is laid down and the turn
  // starts as a forward one, from the right edge at the same height
  const startOf = controller.start.bind(controller)
  const backOnPhone = (point) =>
    render.getOrientation() === "portrait" && controller.getDirectionByPoint(render.convertToBook(point)) === BACK
  // where the finger went down: the drag is measured from it (page-flip takes the page only after 250ms)
  const markFrom = (point) => {
    dragFrom = render.convertToBook(point).x
  }
  controller.start = (point) => {
    if (!backOnPhone(point)) {
      const started = startOf(point)
      if (started) markFrom(point)
      return started
    }
    if (flip.getCurrentPageIndex() < 1) return false
    flip.turnToPrevPage()
    const rect = controller.getBoundsRect()
    if (!startOf({ x: rect.left + 2 * rect.pageWidth - 10, y: point.y })) return false
    reversed = true
    markFrom(point)
    return true
  }
  const resetOf = controller.reset.bind(controller)
  controller.reset = () => {
    reversed = false
    dragFrom = null
    dragAt = null
    resetOf()
  }

  // a drag: the finger's way across the page is the corner's way from turned to down
  const foldOf = controller.fold.bind(controller)
  controller.fold = (point) => {
    // nothing to turn that way (a forward drag on the last page): no fold at all — page-flip set
    // "user_fold" anyway and, with no page taken, the book stayed in it and took no tap again
    if (controller.calc === null && !controller.start(point)) return
    const x = render.convertToBook(point).x
    if (dragFrom === null) dragFrom = x
    dragAt = x
    if (!reversed) return foldOf(point)
    controller.setState("user_fold")
    const rect = controller.getBoundsRect()
    const p = render.convertToPage(point)
    controller.do({ x: 2 * p.x - rect.pageWidth, y: p.y })
  }

  // a tap, the button, a jump (flipPrev): turned to down, where a forward turn goes down to turned
  const flipOf = controller.flip.bind(controller)
  controller.flip = (point) => {
    if (!backOnPhone(point)) return flipOf(point)
    if (controller.calc !== null) render.finishAnimation()
    if (!controller.start(point)) return
    controller.setState("flipping")
    const rect = controller.getBoundsRect()
    const bottom = controller.calc.getCorner() === "bottom"
    const from = { x: 1 - rect.pageWidth, y: bottom ? rect.height - 1 : 1 }
    controller.calc.calc(from)
    controller.animateFlippingTo(from, { x: rect.pageWidth, y: bottom ? rect.height : 0 }, false)
  }

  // a page let go: turned or set back down — page-flip's own rule (past the edge), or on a phone a
  // fifth of the page in the turning direction; the turn is reported (its sound)
  const stopOf = controller.stopMove.bind(controller)
  controller.stopMove = () => {
    if (controller.calc === null) {
      if (controller.getState() !== "read") controller.setState("read")
      return stopOf()
    }
    const rect = controller.getBoundsRect()
    const position = controller.calc.getPosition()
    const y = controller.calc.getCorner() === "bottom" ? rect.height : 0
    const dragged = dragFrom !== null && dragAt !== null ? dragAt - dragFrom : 0
    const far = Math.abs(dragged) > rect.pageWidth * DRAG_TURN
    const portrait = render.getOrientation() === "portrait"
    if (reversed) {
      // a back turn lands when the page comes down (its forward turn falls back): past the middle, or dragged right far enough
      const lands = position.x > 0 || (portrait && far && dragged > 0)
      if (lands) onTurn()
      return controller.animateFlippingTo(position, lands ? { x: rect.pageWidth, y } : { x: -rect.pageWidth, y }, !lands)
    }
    const forward = controller.calc.getDirection() !== BACK
    const turns = position.x <= 0 || (portrait && far && (forward ? dragged < 0 : dragged > 0))
    if (turns) onTurn()
    return controller.animateFlippingTo(position, turns ? { x: -rect.pageWidth, y } : { x: rect.pageWidth, y }, turns)
  }

  return () => {
    controller.start = startOf
    controller.reset = resetOf
    controller.fold = foldOf
    controller.flip = flipOf
    controller.stopMove = stopOf
  }
}

// how much of the way to the cursor a lifted corner goes each frame
const CORNER_EASE = 0.2

/**
 * A lifted page corner that glides after the cursor instead of jumping to it.
 *
 * page-flip lifts a corner when the cursor comes near it (showPageCorners):
 * it folds the corner to a point 50px in, smoothly, and from then on sets the
 * page straight to wherever the cursor is — up to ~160px further in, so the
 * first move jumped, and every move after it jerked; for the hard cover each
 * jump was a tilt of the whole page. Here the drawn point eases towards the
 * cursor frame by frame, once the plugin's own fold has played. Entering and
 * leaving the corner stay the plugin's (both are animated). Returns the stop.
 */
const easeCorners = (flip) => {
  const controller = flip.getFlipController()
  const render = flip.getRender()
  const show = controller.showCorner.bind(controller)
  let target = null
  let point = null
  let frame = 0
  const stop = () => {
    cancelAnimationFrame(frame)
    frame = 0
    point = null
  }
  const step = () => {
    frame = 0
    const calc = controller.getCalculation()
    if (!target || !calc || controller.getState() !== "fold_corner") return stop()
    // the plugin's own fold into the corner is still playing
    if (render.animation) {
      frame = requestAnimationFrame(step)
      return
    }
    if (!point) point = render.convertToGlobal(calc.getPosition())
    point = { x: point.x + (target.x - point.x) * CORNER_EASE, y: point.y + (target.y - point.y) * CORNER_EASE }
    controller.do(render.convertToPage(point))
    if (Math.abs(target.x - point.x) + Math.abs(target.y - point.y) > 0.5) frame = requestAnimationFrame(step)
  }
  controller.showCorner = (pos) => {
    const following = controller.getState() === "fold_corner" && controller.getCalculation() && controller.isPointOnCorners(pos)
    if (!following) {
      target = null
      stop()
      return show(pos)
    }
    target = pos
    if (!frame) frame = requestAnimationFrame(step)
  }
  return stop
}

/**
 * Nothing under a lone cover as it turns.
 *
 * Turning back to the cover (or on to a back cover that stands alone),
 * page-flip names the turning page as the page underneath it too (page-flip
 * 2.0.7, PageCollection.getBottomPage: a spread of one page answers that page
 * for both). Drawn in the same element, the "page underneath" is lost, and
 * where the leaf has lifted off the spread the page it lifted off still
 * shows: page 2 stayed on the left, under the cover coming back over it.
 * Here the page underneath is an empty one in the colour around the book, so
 * the leaf lifts off nothing, as a cover does. Returns the stop.
 */
const nothingUnderCovers = (flip, book) => {
  const collection = flip.getPageCollection()
  const render = flip.getRender()
  const first = flip.getPage(0)
  const HTMLPage = first.constructor
  // the colour the book stands on: the first ancestor that has one
  let backdrop = "transparent"
  for (let node = book.parentElement; node && backdrop === "transparent"; node = node.parentElement) {
    const color = getComputedStyle(node).backgroundColor
    if (color && color !== "transparent" && !/rgba\(.*,\s*0\)$/.test(color)) backdrop = color
  }
  const element = document.createElement("div")
  element.className = "fb-page fb-page--none stf__item"
  element.style.cssText = "display: none"
  first.getElement().parentElement.appendChild(element)
  const none = new HTMLPage(render, element, first.getDensity())
  const paint = () => {
    element.style.background = backdrop
  }

  const bottomOf = collection.getBottomPage.bind(collection)
  collection.getBottomPage = (direction) => {
    const bottom = bottomOf(direction)
    // a single page (a phone) turns back with the same page as both, by design:
    // it is the leaf and what shows as it lands, and it is no cover
    if (render.getOrientation() === "portrait") return bottom
    if (bottom && bottom === collection.getFlippingPage(direction)) return none
    return bottom
  }
  // drawn only while it is the page underneath (page-flip hides only its own pages)
  const drawFrame = render.drawFrame.bind(render)
  render.drawFrame = () => {
    drawFrame()
    if (render.bottomPage === none) return paint()
    element.style.cssText = "display: none"
  }
  return () => {
    collection.getBottomPage = bottomOf
    render.drawFrame = drawFrame
    element.remove()
  }
}

/**
 * One page or two, and how big, for a page `pageWidth`×`pageHeight` in a
 * stage `width`×`height`: two side by side unless each would be narrower than
 * MIN_PAGE_WIDTH, or much smaller than one alone would be (a phone, a tablet
 * held upright).
 */
const layoutFor = (width, height, pageWidth, pageHeight) => {
  const single = Math.min(width / pageWidth, height / pageHeight)
  const double = Math.min(width / (2 * pageWidth), height / pageHeight)
  const portrait = pageWidth * double < MIN_PAGE_WIDTH || double < single * 0.7
  const scale = portrait ? single : double
  const page = { width: Math.floor(pageWidth * scale), height: Math.floor(pageHeight * scale) }
  return { portrait, page, book: { width: portrait ? page.width : page.width * 2, height: page.height } }
}

/**
 * The slider moves by what turns: a page on a phone, a spread where two are side by side — the
 * cover alone, then 2–3, 4–5… (the last spread at its end, as the next button stops there; it moved
 * by page before and stood in the middle on the last spread, and a drag to its end asked for a page
 * already on show: a flip's sound, nothing turning).
 */
const spreadOf = (index, portrait) => (portrait || index <= 0 ? Math.max(0, index) : Math.floor((index + 1) / 2))
const indexOfSpread = (spread, portrait) => (portrait || spread <= 0 ? Math.max(0, spread) : 2 * spread - 1)
const lastSpread = (count, portrait) => Math.max(0, portrait ? count - 1 : Math.floor(count / 2))

/** The pages on show for the page index page-flip reports, as "5–6". */
const pagesLabel = (index, count, portrait) => {
  if (portrait || index === 0) return `${index + 1}`
  const last = Math.min(index + 2, count)
  return last > index + 1 ? `${index + 1}–${last}` : `${index + 1}`
}

export default function FlipBook({ url, title = "", className = "", muted = false }) {
  const t = useT()
  const stageRef = useRef(null)
  const [status, setStatus] = useState("loading")
  const [pageCount, setPageCount] = useState(0)
  const [pageIndex, setPageIndex] = useState(0)
  const [portrait, setPortrait] = useState(false)
  // the preload: the file, then the first pages drawn (0..1)
  const [progress, setProgress] = useState(0)
  const flipRef = useRef(null)
  // read by the book's handlers, which outlive a render
  const mutedRef = useRef(muted)
  // the turn about to be a jump (set by jump(), read by the book's state listener)
  const jumpingRef = useRef(() => {})
  const playSound = useCallback((name) => {
    if (mutedRef.current) return
    const { id, fallback, volume } = SOUNDS[name]
    // a bank without the sample (the jump's, until it is seeded) plays its fallback
    const label = fallback && !SoundBank.hasSample?.(id) ? fallback : id
    SoundBank.play(label, `flipbook-${name}`, { volume })
  }, [])
  mutedRef.current = muted

  // the sounds' bank, named once a book is open (see SOUND_BANK)
  useEffect(() => {
    SoundBank.registerSoundMap(SOUND_BANK, {})
  }, [])
  // the actions the controls call; set once the book is open
  const drawAroundRef = useRef(() => {})

  useEffect(() => {
    if (!url || !stageRef.current) return
    let cancelled = false
    let task = null
    let flip = null
    const images = []
    const drawn = new Set()
    const queue = []
    let drawing = false
    let pdf = null
    let pages = []
    let renderHeight = 1200
    let resizeObserver = null
    // what the book in hand listens to outside itself, taken back with it
    let unlisten = () => {}
    // the phone's taps (set once the book is open), taken back when it closes
    let stopTapsRef = () => {}

    // page `number` drawn onto its element; false when it could not be
    const drawPage = async (number) => {
      try {
        const src = await renderPageImage(pdf, number, renderHeight)
        if (cancelled) {
          URL.revokeObjectURL(src)
          return false
        }
        images.push(src)
        const img = document.createElement("img")
        img.className = "fb-page__image"
        img.alt = `${title} ${number}`
        img.draggable = false
        img.src = src
        // decoded before it goes on the page, so it does not appear in stripes
        await img.decode().catch(() => {})
        if (cancelled) return false
        const page = pages[number - 1]
        page.querySelector(".fb-page__placeholder")?.remove()
        page.appendChild(img)
        return true
      } catch (error) {
        return false
      }
    }

    const drawNext = async () => {
      if (drawing || cancelled) return
      const number = queue.shift()
      if (number === undefined) return
      drawing = true
      // a page that cannot be drawn stays blank, and may be tried again
      if (!(await drawPage(number))) drawn.delete(number)
      drawing = false
      drawNext()
    }

    // the pages on show first, then the ones ahead, then the ones behind
    const drawAround = (index) => {
      const numbers = []
      for (let offset = 0; offset <= AHEAD + 1; offset++) numbers.push(index + 1 + offset)
      for (let offset = 1; offset <= AHEAD; offset++) numbers.push(index + 1 - offset)
      // pages queued for a spread no longer on show go back to undrawn
      for (const number of queue) drawn.delete(number)
      queue.length = 0
      for (const number of numbers) {
        if (number < 1 || number > pages.length || drawn.has(number)) continue
        drawn.add(number)
        queue.push(number)
      }
      drawNext()
    }

    const open = async () => {
      try {
        const [PageFlip, loadingTask] = await Promise.all([loadPageFlip(), openPdf(url)])
        task = loadingTask
        // closed while the engine and the file were on their way
        if (cancelled) return task.destroy()
        // the file's share of the preload: its first part, read as it arrives
        // (PDF.js goes on reporting while it fetches the parts pages are
        // drawn from, so only until the file is open, and only forward)
        let fileOpen = false
        task.onProgress = ({ loaded, total }) => {
          if (fileOpen || cancelled || !total) return
          const share = Math.min(0.3, (loaded / total) * 0.3)
          setProgress((current) => Math.max(current, share))
        }
        pdf = await task.promise
        fileOpen = true
        if (cancelled) return
        const first = await pdf.getPage(1)
        const { width, height } = first.getViewport({ scale: 1 })
        if (cancelled) return

        const stage = stageRef.current
        // sharp on the screen it is read on, without drawing more than a page can show
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        renderHeight = Math.round(Math.min(2200, Math.max(900, stage.clientHeight * ratio)))

        pages = Array.from({ length: pdf.numPages }, (_, i) => {
          const page = document.createElement("div")
          page.className = "fb-page"
          page.innerHTML = `<div class="fb-page__placeholder"><span>${i + 1}</span></div>`
          return page
        })

        // The first pages are drawn before anything is shown: the book
        // appears finished, not as blank pages filling in.
        const preload = Math.min(PRELOAD, pdf.numPages)
        setProgress((current) => Math.max(current, 0.3))
        for (let number = 1; number <= preload; number++) {
          drawn.add(number)
          if (!(await drawPage(number))) drawn.delete(number)
          if (cancelled) return
          setProgress((current) => Math.max(current, 0.3 + (0.7 * number) / preload))
        }

        // The book at `startPage`, sized for the stage as it is now: built
        // again when the stage changes size, on the same page elements.
        let layoutKey = ""
        const build = (startPage) => {
          const layout = layoutFor(stage.clientWidth, stage.clientHeight, width, height)
          if (layout.page.width < 1 || layout.page.height < 1) return
          const key = `${layout.portrait}:${layout.page.width}x${layout.page.height}`
          if (key === layoutKey) return
          layoutKey = key

          unlisten()
          if (flip) {
            flip.destroy()
            flip = null
          }
          stage.querySelector(".fb-book")?.remove()
          // what the last book left on the pages goes with it
          for (const page of pages) {
            page.className = "fb-page"
            page.removeAttribute("style")
          }

          const book = document.createElement("div")
          book.className = "fb-book"
          book.style.width = `${layout.book.width}px`
          book.style.height = `${layout.book.height}px`
          stage.appendChild(book)

          // "fixed": the pages exactly this size, in an element exactly the
          // book's size, so page-flip's own offsets are all zero; one page
          // when the element is narrower than two (usePortrait)
          flip = new PageFlip(book, {
            width: layout.page.width,
            height: layout.page.height,
            size: "fixed",
            autoSize: false,
            usePortrait: true,
            showCover: true,
            startPage,
            drawShadow: true,
            maxShadowOpacity: 0.5,
            flippingTime: FLIP_TIME,
            mobileScrollSupport: false,
            showPageCorners: true,
          })
          flip.on("flip", (event) => {
            setPageIndex(event.data)
            drawAround(event.data)
          })
          // the corner easing wraps the plugin's controller, made by loadFromHTML
          let stopEasing = () => {}
          let stopCovers = () => {}
          let stopCurl = () => {}
          let folding = false
          const sound = playSound
          // a jump (jump() below) has played its own sound: its turn is not heard again;
          // and a turn is heard once, however it was set off (a release that turns, the "flipping" state)
          let jumping = false
          let turned = false
          const turn = () => {
            if (jumping || turned) return
            turned = true
            sound("flip")
          }
          // the corner lifting — under the cursor, or taken — is the peel; the turn that
          // may follow is the flip
          flip.on("changeState", (event) => {
            const state = event.data
            if ((state === "fold_corner" || state === "user_fold") && !folding) {
              folding = true
              sound("peel")
            }
            if (state === "flipping") turn()
            if (state === "read") {
              folding = false
              jumping = false
              turned = false
            }
          })
          jumpingRef.current = () => {
            jumping = true
          }
          // a page let go of after a drag: heard only when it turns (curlBackTurns decides at the
          // release) — one that falls back, or has no page to turn to, is silent
          unlisten = () => {
            stopEasing()
            stopCovers()
            stopCurl()
          }
          flip.loadFromHTML(pages)
          // a magazine: the cover is paper like the rest and turns as they do
          // (showCover makes it a hard board; it stays shown alone)
          for (let i = 0; i < flip.getPageCount(); i++) flip.getPage(i).setDensity("soft")
          tightenCorners(flip)
          stopEasing = easeCorners(flip)
          stopCovers = nothingUnderCovers(flip, book)
          stopCurl = curlBackTurns(flip, { onTurn: turn })
          flipRef.current = flip
          setPortrait(layout.portrait)
        }

        build(0)
        drawAroundRef.current = drawAround

        // a quick tap on a phone turns the page — the right part forward, the left part back —
        // as a reader expects (page-flip takes a touch only once held 250ms: a tap did nothing);
        // a swipe and a drag stay page-flip's
        let tap = null
        const onTouchStart = (event) => {
          const touch = event.touches[0]
          tap = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY, at: Date.now() } : null
        }
        const onTouchEnd = (event) => {
          const touch = event.changedTouches[0]
          const begun = tap
          tap = null
          // not while a page turns or is held (a corner left lifted after a fall-back still takes a tap)
          if (!begun || !touch || !flip || ["flipping", "user_fold"].includes(flip.getState())) return
          if (Date.now() - begun.at > 240 || Math.abs(touch.clientX - begun.x) > 10 || Math.abs(touch.clientY - begun.y) > 10) return
          const rect = stage.querySelector(".fb-book")?.getBoundingClientRect()
          if (!rect || touch.clientX < rect.left || touch.clientX > rect.right || touch.clientY < rect.top || touch.clientY > rect.bottom) return
          const at = (touch.clientX - rect.left) / rect.width
          if (at > 0.6) flip.flipNext(touch.clientY - rect.top < rect.height / 2 ? "top" : "bottom")
          else if (at < 0.4) flip.flipPrev(touch.clientY - rect.top < rect.height / 2 ? "top" : "bottom")
        }
        stage.addEventListener("touchstart", onTouchStart, { passive: true })
        stage.addEventListener("touchend", onTouchEnd, { passive: true })
        const stopTaps = () => {
          stage.removeEventListener("touchstart", onTouchStart)
          stage.removeEventListener("touchend", onTouchEnd)
        }
        stopTapsRef = stopTaps
        setPageCount(pdf.numPages)
        setPageIndex(0)
        setStatus("ready")
        drawAround(0)

        // a turned phone, a resized window: the book again, on the same page
        let resizeTimer = null
        const rebuild = () => {
          clearTimeout(resizeTimer)
          resizeTimer = setTimeout(() => {
            if (cancelled || !flip) return
            // not in the middle of a turn: once it has landed
            if (flip.getState() !== "read") return rebuild()
            build(flip.getCurrentPageIndex())
          }, 150)
        }
        resizeObserver = new ResizeObserver(rebuild)
        resizeObserver.observe(stage)
      } catch (error) {
        console.error("[FlipBook]", error)
        if (!cancelled) setStatus("error")
      }
    }
    open()

    return () => {
      cancelled = true
      resizeObserver?.disconnect()
      unlisten()
      stopTapsRef()
      flipRef.current = null
      drawAroundRef.current = () => {}
      try {
        flip?.destroy()
      } catch (_) {
        // already gone with its element
      }
      stageRef.current?.querySelector(".fb-book")?.remove()
      images.forEach((src) => src.startsWith("blob:") && URL.revokeObjectURL(src))
      task?.destroy()
    }
  }, [url])

  const prev = useCallback(() => flipRef.current?.flipPrev(), [])
  const next = useCallback(() => flipRef.current?.flipNext(), [])
  // to a page further than the next spread: turned to (not set down), with the
  // jump's sound, as heyzine does; the pages there are drawn first
  const jump = useCallback((index) => {
    const flip = flipRef.current
    if (!flip) return
    const from = flip.getCurrentPageIndex()
    // the spread already on show: nothing to turn (and nothing to hear)
    const portraitNow = flip.getOrientation?.() === "portrait"
    if (spreadOf(index, portraitNow) === spreadOf(from, portraitNow)) return
    drawAroundRef.current(index)
    if (Math.abs(index - from) <= 2) return flip.flip(index)
    jumpingRef.current()
    playSound("jump")
    flip.flip(index)
    setPageIndex(index)
  }, [playSound])

  // the arrow keys turn the pages, as they would in a reader
  useEffect(() => {
    if (status !== "ready") return
    const onKey = (event) => {
      if (event.target?.tagName === "INPUT" && event.target.type !== "range") return
      if (event.key === "ArrowLeft") prev()
      if (event.key === "ArrowRight") next()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [status, prev, next])

  const atStart = pageIndex <= 0
  const atEnd = pageIndex >= pageCount - (portrait ? 1 : 2)

  return (
    <div className={`fb ${className}`} data-testid="flipbook">
      <div className="fb__stage-wrap">
        {status === "ready" && (
          <button type="button" className="fb__side fb__side--prev" onClick={prev} disabled={atStart} aria-label={t`Previous page`}>
            <ChevronLeftIcon className="w-7 h-7" />
          </button>
        )}
        <div ref={stageRef} className="fb__stage" />
        {status === "ready" && (
          <button type="button" className="fb__side fb__side--next" onClick={next} disabled={atEnd} aria-label={t`Next page`}>
            <ChevronRightIcon className="w-7 h-7" />
          </button>
        )}

        {status === "loading" && (
          <div className="fb__overlay" data-testid="flipbook-loading">
            <div className="fb__spinner" />
            <span>{t`Opening the book…`}</span>
            <div className="fb__progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
              <div className="fb__progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="fb__overlay">
            <span>{t`The preview could not be opened.`}</span>
            <a href={url} target="_blank" rel="noreferrer" className="fb__link">
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              {t`Open the PDF`}
            </a>
          </div>
        )}
      </div>

      {status === "ready" && (
        <div className="fb__bar">
          <button type="button" className="fb__button" onClick={prev} disabled={atStart} aria-label={t`Previous page`}>
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <input
            type="range"
            className="fb__slider"
            min={0}
            max={lastSpread(pageCount, portrait)}
            value={Math.min(spreadOf(pageIndex, portrait), lastSpread(pageCount, portrait))}
            onChange={(event) => jump(indexOfSpread(Number(event.target.value), portrait))}
            aria-label={t`Page`}
          />
          <span className="fb__count" data-testid="flipbook-page">
            {pagesLabel(pageIndex, pageCount, portrait)} / {pageCount}
          </span>
          <button type="button" className="fb__button" onClick={next} disabled={atEnd} aria-label={t`Next page`}>
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  )
}
